import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';

interface IoBoldsignPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentUrl: string | null;
  onApproveAndSend: () => void;
  onBackToEdit: () => void;
  isLoading?: boolean;
  viewOnly?: boolean;
}

function getOptimisedPdfUrl(url: string | null): string | undefined {
  if (!url) return undefined;
  const timestamp = Date.now();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}zoom=100&view=FitH&pagemode=none&t=${timestamp}`;
}

export function IoBoldsignPreviewModal({
  open,
  onOpenChange,
  documentUrl,
  onApproveAndSend,
  onBackToEdit,
  isLoading = false,
  viewOnly = false,
}: IoBoldsignPreviewModalProps) {
  const [pdfError, setPdfError] = useState(false);
  const optimisedDocumentUrl = getOptimisedPdfUrl(documentUrl);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className={cn(portalDs.modal, 'gap-0 p-0 sm:max-h-[95vh] sm:max-w-[95vw]')}>
        <DialogHeader className={portalDs.modalHeader}>
          <DialogTitle className={portalDs.modalTitle}>
            {viewOnly ? 'View Insertion Order' : 'Preview Insertion Order'}
          </DialogTitle>
        </DialogHeader>

        <div className={cn(portalDs.modalBody, 'min-h-[70vh] pt-0')}>
          <div className="h-full min-h-[70vh] rounded-lg border border-[#EDEEF2] bg-[#F7F8FA]">
            {!documentUrl || pdfError ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className={cn(portalDs.bodySm, 'mb-2')}>
                    No document preview available.
                  </p>
                  <p className={portalDs.caption}>
                    The document may still be generating. Please try again later.
                  </p>
                </div>
              </div>
            ) : (
              <iframe
                src={optimisedDocumentUrl || undefined}
                className="h-full w-full rounded-lg"
                title="Insertion Order Preview"
                onError={() => setPdfError(true)}
                onLoad={() => setPdfError(false)}
              />
            )}
          </div>
        </div>

        {!viewOnly ? (
          <DialogFooter className={cn(portalDs.modalFooter, 'sm:space-x-0')}>
            <PortalDsButton
              variant="secondary"
              onClick={onBackToEdit}
              disabled={isLoading}
            >
              Back to Edit
            </PortalDsButton>
            <PortalDsButton
              variant="accent"
              onClick={onApproveAndSend}
              disabled={isLoading || !documentUrl}
            >
              {isLoading ? 'Processing...' : 'Approve & Send'}
            </PortalDsButton>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
